import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useStreamChat } from '../context/StreamChatContext';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import StreamChatBox from '../components/Chat/StreamChatBox';
import { useOptionalSidebar } from '../components/ui/sidebar';

const StreamChat = () => {
  const { chatClient, loading, error, initializeChat, user } = useStreamChat();
  const { userSettings } = useUserSettingsContext();
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  // New look (desktop and phone): fill the content card (full-bleed route).
  // The Classic mobile layout has no sidebar context and keeps the fixed
  // between-bars panel.
  const sidebar = useOptionalSidebar();
  const inShell = !!sidebar;

  useEffect(() => {
    const originalViewport = document.querySelector('meta[name="viewport"]')?.content;
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    }

    const appleMobileWebAppCapable = document.createElement('meta');
    appleMobileWebAppCapable.setAttribute('name', 'apple-mobile-web-app-capable');
    appleMobileWebAppCapable.setAttribute('content', 'yes');
    const mobileWebAppCapable = document.createElement('meta');
    mobileWebAppCapable.setAttribute('name', 'mobile-web-app-capable');
    mobileWebAppCapable.setAttribute('content', 'yes');
    document.head.appendChild(appleMobileWebAppCapable);
    document.head.appendChild(mobileWebAppCapable);

    return () => {
      if (viewportMeta && originalViewport) viewportMeta.setAttribute('content', originalViewport);
      const appleMeta = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
      const mobileMeta = document.querySelector('meta[name="mobile-web-app-capable"]');
      if (appleMeta) document.head.removeChild(appleMeta);
      if (mobileMeta) document.head.removeChild(mobileMeta);
    };
  }, []);

  const getUserIdForChat = useCallback(() => {
    if (userSettings) {
      if (userSettings.cognitoId) return userSettings.cognitoId;
      if (userSettings.cognitoUsername) return userSettings.cognitoUsername;
      if (userSettings.email) return userSettings.email;
    }
    const userPoolId = localStorage.getItem('amplify-signin-with-webui');
    if (userPoolId) {
      try {
        const userData = JSON.parse(localStorage.getItem(userPoolId));
        if (userData?.username) return userData.username;
      } catch (error) {
        console.error('[StreamChat] Error parsing user data from localStorage:', error);
      }
    }
    return localStorage.getItem('userId') || null;
  }, [userSettings]);

  const userId = useMemo(() => getUserIdForChat(), [getUserIdForChat]);

  useEffect(() => {
    if (userSettings?.email && !chatClient && !loading) {
      initializeChat(userSettings.email, userSettings);
    }
  }, [userSettings, chatClient, loading, initializeChat]);

  useEffect(() => {
    let reconnectTimer;
    if (error && userSettings?.email && reconnectAttempt < 5) {
      const backoff = Math.min(30000, 1000 * Math.pow(2, reconnectAttempt));
      reconnectTimer = setTimeout(() => {
        setReconnectAttempt(prev => prev + 1);
        initializeChat(userSettings.email, userSettings);
      }, backoff);
    }
    return () => { if (reconnectTimer) clearTimeout(reconnectTimer); };
  }, [error, userSettings, reconnectAttempt, initializeChat]);

  return (
    <div style={inShell ? {
      position: 'relative',
      height: 'calc(100svh - var(--shell-sticky-top) - var(--shell-gap) - 2px)',
      background: 'var(--lodge-white)',
      fontFamily: 'var(--font-sans)',
    } : {
      position: 'fixed',
      inset: 0,
      top: 64,
      bottom: 64,
      background: 'var(--lodge-white)',
      fontFamily: 'var(--font-sans)',
    }}>
      <div style={{ height: '100%', width: '100%' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 32 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{
                width: 40, height: 40,
                border: '3px solid var(--lodge-onyx-100)',
                borderTopColor: 'var(--lodge-orange-400)',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <p style={{ marginTop: 16, color: 'var(--lodge-onyx-500)', fontSize: 14 }}>Connecting to Stream Chat...</p>
            </div>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 24 }}>
            <div style={{ background: 'var(--color-error-bg)', color: 'var(--color-error)', padding: 16, borderRadius: 'var(--radius-lg)', marginBottom: 16 }}>
              <p style={{ margin: 0 }}>Could not connect to Stream Chat: {error?.message || 'Unknown error'}</p>
              <p style={{ fontSize: 13, marginTop: 8, margin: 0 }}>
                Attempt {reconnectAttempt}/5 - {reconnectAttempt < 5 ? 'Reconnecting soon...' : 'Maximum attempts reached.'}
              </p>
            </div>
            <button
              onClick={() => {
                if (userSettings?.email) {
                  setReconnectAttempt(0);
                  initializeChat(userSettings.email, userSettings);
                }
              }}
              style={{
                padding: '8px 16px',
                background: 'var(--lodge-orange-400)',
                color: 'white',
                borderRadius: 'var(--radius-md)',
                fontWeight: 600,
                fontSize: 14,
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
            >
              Retry Connection
            </button>
          </div>
        ) : (
          <div style={{ position: 'absolute', inset: 0 }}>
            <StreamChatBox isOpen={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamChat;
